/**
 * StorageService - Unified Supabase storage for Concal v2
 * 
 * Consolidates all file storage operations:
 * - Contract PDFs
 * - Logo uploads
 * - File retrieval
 * - URL generation
 * 
 * Replaces:
 * - fileStorageManager.js
 * - logoStorageManager.js
 */

import { supabase } from '../lib/supabase';

class StorageService {
  /**
   * Upload a contract PDF file
   * 
   * @param {File} file - PDF file object
   * @param {string} userId - User ID for folder organization
   * @param {string} dealId - Deal ID (optional, for naming)
   * @returns {Promise<Object>} Upload result {success, url, path, error}
   */
  static async uploadContract(file, userId, dealId = null) {
    try {
      // Validate file type
      if (file.type !== 'application/pdf') {
        return {
          success: false,
          error: 'Only PDF files are allowed for contracts'
        };
      }

      // Validate file size (max 10MB)
      const MAX_SIZE = 10 * 1024 * 1024; // 10MB
      if (file.size > MAX_SIZE) {
        return {
          success: false,
          error: 'File size exceeds 10MB limit'
        };
      }

      // Generate unique filename
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = dealId 
        ? `${dealId}_${timestamp}_${sanitizedName}`
        : `${timestamp}_${sanitizedName}`;
      
      const filePath = `${userId}/contracts/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('contracts')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Contract upload error:', error);
        return {
          success: false,
          error: error.message
        };
      }

      // Get public URL
      const { data: publicData } = supabase.storage
        .from('contracts')
        .getPublicUrl(filePath);

      return {
        success: true,
        url: publicData.publicUrl,
        path: filePath,
        fileName: fileName
      };

    } catch (error) {
      console.error('Contract upload exception:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Upload a logo file
   * 
   * @param {File} file - Image file (PNG, JPG, SVG)
   * @param {string} dealId - Deal ID for folder organization
   * @returns {Promise<Object>} Upload result {success, url, path, error}
   */
  static async uploadLogo(file, dealId) {
    try {
      // Validate file type
      const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!ALLOWED_TYPES.includes(file.type)) {
        return {
          success: false,
          error: 'Only PNG, JPG, and SVG files are allowed for logos'
        };
      }

      // Validate file size (max 2MB)
      const MAX_SIZE = 2 * 1024 * 1024; // 2MB
      if (file.size > MAX_SIZE) {
        return {
          success: false,
          error: 'Logo size exceeds 2MB limit'
        };
      }

      // Generate unique filename
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${timestamp}_${sanitizedName}`;
      const filePath = `${dealId}/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('logos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Logo upload error:', error);
        return {
          success: false,
          error: error.message
        };
      }

      // Get public URL
      const { data: publicData } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      return {
        success: true,
        url: publicData.publicUrl,
        path: filePath,
        fileName: fileName
      };

    } catch (error) {
      console.error('Logo upload exception:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Delete a contract file
   * 
   * @param {string} filePath - Full path to file in storage
   * @returns {Promise<Object>} Delete result {success, error}
   */
  static async deleteContract(filePath) {
    try {
      const { data, error } = await supabase.storage
        .from('contracts')
        .remove([filePath]);

      if (error) {
        console.error('Contract delete error:', error);
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: true
      };

    } catch (error) {
      console.error('Contract delete exception:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Delete a logo file
   * 
   * @param {string} filePath - Full path to file in storage
   * @returns {Promise<Object>} Delete result {success, error}
   */
  static async deleteLogo(filePath) {
    try {
      const { data, error } = await supabase.storage
        .from('logos')
        .remove([filePath]);

      if (error) {
        console.error('Logo delete error:', error);
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: true
      };

    } catch (error) {
      console.error('Logo delete exception:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get public URL for a contract
   * 
   * @param {string} filePath - Full path to file in storage
   * @returns {string} Public URL
   */
  static getContractUrl(filePath) {
    const { data } = supabase.storage
      .from('contracts')
      .getPublicUrl(filePath);

    return data.publicUrl;
  }

  /**
   * Get public URL for a logo
   * 
   * @param {string} filePath - Full path to file in storage
   * @returns {string} Public URL
   */
  static getLogoUrl(filePath) {
    const { data } = supabase.storage
      .from('logos')
      .getPublicUrl(filePath);

    return data.publicUrl;
  }

  /**
   * List all contracts for a user
   * 
   * @param {string} userId - User ID
   * @returns {Promise<Array>} List of contract files
   */
  static async listUserContracts(userId) {
    try {
      const { data, error } = await supabase.storage
        .from('contracts')
        .list(`${userId}/contracts`, {
          limit: 100,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        console.error('List contracts error:', error);
        return [];
      }

      return data || [];

    } catch (error) {
      console.error('List contracts exception:', error);
      return [];
    }
  }

  /**
   * Download a contract file
   * 
   * @param {string} filePath - Full path to file in storage
   * @returns {Promise<Blob>} File blob
   */
  static async downloadContract(filePath) {
    try {
      const { data, error } = await supabase.storage
        .from('contracts')
        .download(filePath);

      if (error) {
        console.error('Download contract error:', error);
        throw new Error(error.message);
      }

      return data;

    } catch (error) {
      console.error('Download contract exception:', error);
      throw error;
    }
  }
}

export default StorageService;